import { Placement } from '@popperjs/core';
import React, { ReactElement, useCallback, useRef, useState, VFC } from 'react';
import { ChangeOwnershipPanelTypeMapping, useChangeOwnershipStore } from 'src/common/components/change-ownership/store';
import { Journey } from 'src/common/interfaces/journey.interface';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { JourneyMenu } from 'src/common/JourneyMenu';
import { EditorConfirmPanel } from 'src/editor/confirm-panel';
import { DeleteRegularIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import delay from 'lodash/delay';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { apiGetUserJourneys } from 'src/utils/journeyApi';
import { shallow } from 'zustand/shallow';
import { useSlideOverStore } from 'src/store/slide-over';

interface DashboardSettingsMembersOptionsProps {
  onMenuOpen: () => void;
  onMenuClose: () => void;
  renderMenuOrigin: (onClick: () => void) => ReactElement;
  nestedMenuPlacement?: Placement;
  onRevokeUserConfirm: () => void;
  userId: string;
}

export interface UserJourneysResponseInterface {
  id: Journey['id'];
  name: Journey['name'];
  workspace_id: Workspace['id'];
}

export const DashboardSettingsMembersOptions: VFC<DashboardSettingsMembersOptionsProps> = ({
  onMenuOpen,
  onMenuClose,
  onRevokeUserConfirm,
  userId,
  renderMenuOrigin,
  nestedMenuPlacement,
}) => {
  const [activePanel, setActivePanel] = useState<Nullable<'revoke-user'>>(null);
  const openSlideOver = useSlideOverStore((state) => state.openSlideOver);

  const { currentOrganization } = useCurrentOrganization();

  const settingsMenuOpenRef = useRef(functionNoop);
  const settingsMenuCloseRef = useRef(functionNoop);

  const revokeUserPanelOpenRef = useRef(functionNoop);
  const revokeUserPanelCloseRef = useRef(functionNoop);

  const [loading, setLoading] = useState(false);

  const {
    appendJourneyOwnerShip,
    setUserNameToBeRevoked,
    setOwnerShipPanelType,
    resetChangeOwnerShipStore,
    onOwnerShipUpdated,
  } = useChangeOwnershipStore(
    (state) => ({
      appendJourneyOwnerShip: state.appendJourneyOwnerShip,
      setOwnerShipPanelType: state.setPanelType,
      resetChangeOwnerShipStore: state.reset,
      onOwnerShipUpdated: state.onOwnerShipUpdated,
      setUserNameToBeRevoked: state.setUserNameToBeRevoked,
    }),
    shallow
  );

  const getWorkspaceMembers = (workspaceId: Workspace['id']) =>
    currentOrganization.workspaces.find((workspace: Workspace) => workspace.id === workspaceId)?.members || [];

  const onMenuClicked = () => {
    settingsMenuOpenRef.current();
  };

  const onBack = () => {
    setActivePanel(null);
    revokeUserPanelOpenRef.current();
    setTimeout(onMenuClicked, 50);
  };

  const onClosePanel = () => {
    setActivePanel(null);
    onMenuClose();
  };

  const onMenuItemClicked = useCallback(() => {
    setOwnerShipPanelType(ChangeOwnershipPanelTypeMapping.SETTINGS);
    setActivePanel('revoke-user');
  }, []);

  const onRevokeCompleted = () => {
    onRevokeUserConfirm();
    onClosePanel();
    resetChangeOwnerShipStore();
  };

  if (activePanel === 'revoke-user') {
    const onRevokeConfirmed = async () => {
      setLoading(true);
      const journeys: Journey[] = await apiGetUserJourneys(currentOrganization.id, userId);
      setLoading(false);
      let totalWorkspaceMembers = 0;
      if (journeys.length > 0) {
        journeys.forEach((journey) => {
          const journeyWorkspaceMembers = getWorkspaceMembers(journey.workspace.id);
          totalWorkspaceMembers += journeyWorkspaceMembers.length;
          appendJourneyOwnerShip(journey, journeyWorkspaceMembers);
        });
        onOwnerShipUpdated(onRevokeCompleted);
        if (!(totalWorkspaceMembers >= 1)) {
          onRevokeCompleted();
          return;
        }
        delay(() => {
          const userToBeRevoked = currentOrganization.users?.find((user: any) => user.id === parseInt(userId, 10));
          if (userToBeRevoked) {
            setUserNameToBeRevoked(userToBeRevoked.name);
          }
          openSlideOver('ownership');
        }, 3_00);
      } else {
        onRevokeCompleted();
      }
    };

    return (
      <EditorConfirmPanel
        confirmCopy='Yes, revoke'
        denyCopy='No, keep'
        copy='Revoking this user is permanent. You will need to reinvite them if you wish.'
        openRef={revokeUserPanelOpenRef}
        isConfirmLoading={loading}
        closeRef={revokeUserPanelCloseRef}
        disableConfirmButton={loading}
        disableDenyButton={loading}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        placement={nestedMenuPlacement}
        onOpen={onMenuOpen}
        onClose={onClosePanel}
        onConfirm={onRevokeConfirmed}
        onDeny={onClosePanel}
        onBack={onBack}
      />
    );
  }

  return (
    <JourneyMenu
      onOpen={onMenuOpen}
      openRef={settingsMenuOpenRef}
      placement={'bottom-start'}
      closeRef={settingsMenuCloseRef}
      onClose={onMenuClose}
      renderOrigin={() => renderMenuOrigin(onMenuClicked)}
    >
      <JourneyMenu.Item className='text-bedrock-p text-bedrock-black text-left' onClick={onMenuItemClicked}>
        <div className='flex items-center space-x-sm'>
          <DeleteRegularIcon />
          <span className='text-bedrock-p text-bedrock-black'>Revoke user</span>
        </div>
      </JourneyMenu.Item>
    </JourneyMenu>
  );
};
